import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Animated, FlatList, Platform, View, StatusBar } from 'react-native';

import InvoiceItem from './components/InvoiceItem';
import Header from './components/Header';
import { getCredsystemCardInvoiceDetails } from '~/credsystem/useCases';
import type { InvoiceDetails } from '~/credsystem/domain/invoices';
import Skeleton from '~/components/Skeleton';
import Loader from './components/Loader';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { isWeb } from '~/utils/device';
import colors from '~/assets/themes/colors';

const ItemSeparator = () => <View className="h-[8px]" />;

const Invoices = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceDetails[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<StatementType | null>(
    null
  );
  const { bottom } = useSafeAreaInsets();

  const flatListRef = useRef<FlatList>(null);
  const scrollY = useRef(new Animated.Value(0));
  const translateYNumber = useRef();

  const headerHeight = useMemo(() => {
    if (selectedInvoice?.hasBill) {
      return Platform.select({ web: 435, ios: 470, android: 440 }) ?? 425;
    }
    return Platform.select({ web: 380, ios: 400, android: 370 }) ?? 350;
  }, [selectedInvoice?.hasBill]);

  const scrollYClamped = Animated.diffClamp(scrollY.current, 0, headerHeight);

  const defaultHeaderOpacity = scrollYClamped.interpolate({
    inputRange: [0, headerHeight / 2],
    outputRange: [1, 0],
  });

  const collapsedHeaderOpacity = scrollYClamped.interpolate({
    inputRange: [0, headerHeight / 2, headerHeight],
    outputRange: [0, 0, 1],
  });

  const translateY = scrollYClamped.interpolate({
    inputRange: [0, headerHeight],
    outputRange: [0, -(headerHeight / 2.1)],
  });

  translateY.addListener(({ value }) => {
    translateYNumber.current = value;
  });

  const handleScroll = Animated.event(
    [
      {
        nativeEvent: {
          contentOffset: { y: scrollY.current },
        },
      },
    ],
    {
      useNativeDriver: true,
    }
  );

  const loadInvoiceDetails = useCallback(
    async (selectedMonth: StatementType) => {
      if (selectedMonth) {
        setLoading(true);
        setSelectedInvoice(selectedMonth);
        const response = await getCredsystemCardInvoiceDetails(
          selectedMonth.statementId
        );
        setInvoices(response ?? []);
        setLoading(false);
      }
    },
    []
  );

  return (
    <SafeAreaView
      className="flex-1 container mx-auto max-w-5xl"
      style={{ backgroundColor: colors.credsystem.light[200] }}
    >
      <StatusBar barStyle={'light-content'} />
      <Animated.View
        className="absolute left-0 right-0 w-full z-[1]"
        style={{ transform: [{ translateY }] }}
      >
        <Header
          onChangeMonth={loadInvoiceDetails}
          height={headerHeight}
          opacity={{
            default: defaultHeaderOpacity,
            collapsed: collapsedHeaderOpacity,
          }}
        />
      </Animated.View>
      <Animated.FlatList
        ref={flatListRef}
        data={invoices}
        keyExtractor={({ contract }) => String(contract)}
        renderItem={({ item, index }) => (
          <Skeleton loading={loading} loader={<Loader />}>
            <View
              className={`px-6 ${isWeb && index === 0 && 'mt-4'} ${
                Platform.OS === 'ios' && index === 0 && 'mt-[-24px]'
              }`}
            >
              <InvoiceItem data={item} />
            </View>
          </Skeleton>
        )}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        contentContainerStyle={{
          paddingTop: headerHeight,
        }}
        ItemSeparatorComponent={ItemSeparator}
        ListFooterComponent={ItemSeparator}
        ListFooterComponentStyle={{ height: bottom - 8 }}
        showsVerticalScrollIndicator={false}
        bounces={false}
      />
    </SafeAreaView>
  );
};

export default Invoices;
